import React, { ReactElement } from 'react';
import styled, { StyledComponent } from '@emotion/styled';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import Form from './Form';
import { useThemeContext } from './ThemeContext';
import { useFormContext } from 'react-hook-form';
const logo = require('../Logo/logo.svg').default;
const e = require('../Logo/icon.svg').default;
const logoReversed = require('../Logo/logo_reversed.svg').default;

interface LogoProps {
  className?: string;
  justE?: boolean;
  reversed?: boolean;
}

interface BasicProps {
  className?: string;
  children?: React.ReactNode;
}

interface CredsComponents extends React.FC<BasicProps> {
  Logo: React.FC<LogoProps>;
  Container: React.FC<BasicProps>;
  Body: StyledComponent<BasicProps, any, any>;
  Title: StyledComponent<BasicProps, any, any>;
  Note: StyledComponent<BasicProps, any, any>;
  Subtitle: StyledComponent<BasicProps, any, any>;
  Separator: StyledComponent<BasicProps, any, any>;
  Link: StyledComponent<BasicProps, any, any>;
  SubmitButton: React.FC<BasicProps>;
}

const Creds: CredsComponents = ({ children, className }) => (
  <div
    className={className}
    css={css`
      ${tw`font-normal font-body text-color-input-field`}
      a:hover {
        text-decoration: underline;
      }
    `}
  >
    {children}
  </div>
);

Creds.Logo = ({ className, justE, reversed }): ReactElement => {
  return (
    <img
      height='24px'
      css={css`
        height: 2rem;
        ${justE ? '' : 'margin-top: 0.5rem;'}
      `}
      alt='eWebinar logo'
      className={`logo object-fit ${className}`}
      src={justE ? e : reversed ? logoReversed : logo}
    />
  );
};

Creds.Container = ({ children, className }): ReactElement => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { md: isMd } = useWindowSizeInfo();

  return (
    <div className='h-100vh container justify-center mx-auto items-center flex'>
      <div
        className={`w-full mx-auto flex flex-col items-center text-lg ${className}`}
        css={css`
          background: white;
          padding: 20px;
          ${isMd
            ? `
              max-width: 34rem;
              `
            : ''}
          .logo {
            margin-bottom: 3rem;
          }
        `}
      >
        {children}
      </div>
    </div>
  );
};

Creds.Note = styled.div`
  ${tw`text-sm text-color-setting leading-snug mb-6`}
`;

Creds.Title = styled.div`
  ${tw`font-heading font-semibold leading-tight text-coral-1 text-2xl md:text-4xl text-center`}
  margin: calc(2rem - .14285714em) 0 1rem;
  &:first-of-type {
    margin-top: 0;
  }
  & + * {
    margin-top: 1rem;
  }
  & + ${Creds.Note} {
    margin-top: 0.5rem;
  }
`;

Creds.Body = styled.div`
  ${tw`text-lg text-color-setting leading-snug mb-6`}
`;

Creds.Subtitle = styled.div`
  ${tw`font-heading font-normal leading-none text-xl md:text-2xl`}
  margin: calc(2rem - .14285714em) 0 1rem;
  line-height: 2rem;

  &:first-of-type {
    margin-top: 0;
  }

  & + * {
    margin-top: 1rem;
  }
  & + ${Creds.Note} {
    margin-top: 0.5rem;
  }
`;

Creds.Separator = styled.div`
  ${tw`border-t border-gray-200 mb-3 mt-3`}
`;
Creds.Link = styled.span`
  ${tw`mr-auto text-color-brand font-semibold leading-none whitespace-nowrap`}
`;

const SubmitButton: CredsComponents['SubmitButton'] = ({ className, children }): ReactElement => {
  const etheme = useThemeContext();
  const formHandlers = useFormContext();
  return (
    <Form.SubmitButton
      className={className}
      css={[
        tw`py-2/3e w-full font-semibold text-lg`,
        css`
          min-width: 13rem;
        `,
      ]}
      theme={
        formHandlers.formState.isValid
          ? etheme.controls.button.action
          : etheme.controls.button.invalid
      }
    >
      {children}
    </Form.SubmitButton>
  );
};

Creds.SubmitButton = SubmitButton;

export default Creds;
